import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Bell, Activity, ArrowUpRight, ArrowDownRight, 
  Crown, RefreshCw, Clock, Globe, Play, User, Target, AlertCircle, 
  Calendar, BarChart3, BookOpen, Gauge, Tv, Zap, ChevronRight, Flame,
  Eye, DollarSign, Percent, Shield
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import LiveStreamGrid from '../components/LiveStreamGrid';
import EconomicCalendar from '../components/EconomicCalendar';
import PerformanceDashboard from '../components/PerformanceDashboard';
import TradingJournal from '../components/TradingJournal';
import MarketSentiment from '../components/MarketSentiment';
import Sparkline from '../components/Sparkline';
import AIMarketAnalyst from '../components/AIMarketAnalyst';
import TradingPanel from '../components/TradingPanel';
import WelcomeTour from '../components/WelcomeTour';
import { API, getAuthHeaders } from '../utils/api';
import React from 'react';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error('Component error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

// Placeholder for errored premium components
const PremiumPlaceholder = ({ title }) => (
  <Card className="bg-[#0D131C] border-[#1E293B] p-6">
    <div className="text-center">
      <AlertCircle className="w-8 h-8 text-slate-500 mx-auto mb-2" />
      <p className="text-slate-400">{title} loading...</p>
    </div>
  </Card>
);

const FALLBACK_PRICES = [
  { name: 'XAUUSD', price: '3,285.00', raw_price: 3285.00, change: '+0.45%', trend: 'up' },
  { name: 'EURUSD', price: '1.1628', raw_price: 1.1628, change: '+0.12%', trend: 'up' },
  { name: 'GBPUSD', price: '1.3459', raw_price: 1.3459, change: '-0.08%', trend: 'down' },
  { name: 'USDJPY', price: '145.82', raw_price: 145.82, change: '+0.22%', trend: 'up' }
];

const API_TIMEOUT = 8000;

const trackActivity = async (activityType, activityData = null) => {
  try {
    await axios.post(
      `${API}/activity/track?activity_type=${activityType}`,
      { activity_data: activityData },
      { withCredentials: true, headers: getAuthHeaders(), timeout: 5000 }
    );
  } catch (e) {}
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_signals: 0, active_signals: 0, win_rate: 0 });
  const [recentSignals, setRecentSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesError, setPricesError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const prevPricesRef = useRef({});
  const [isPro, setIsPro] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [marketHours, setMarketHours] = useState(null);
  const [signalPerformance, setSignalPerformance] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [liveMarkets, setLiveMarkets] = useState(FALLBACK_PRICES);
  const [showTour, setShowTour] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [chartMarket, setChartMarket] = useState(null);   // opened market for the sparkline modal
  const [chartRange, setChartRange] = useState('24h');    // 1h | 24h | 7d

  // Check if should show tour
  useEffect(() => {
    const tourCompleted = localStorage.getItem('tour_completed');
    if (!tourCompleted) {
      setShowTour(true);
    }
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, signalsRes, subRes, hoursRes, perfRes, profileRes] = await Promise.allSettled([
          axios.get(`${API}/stats`, { withCredentials: true, headers: getAuthHeaders(), timeout: API_TIMEOUT }),
          axios.get(`${API}/signals?limit=5&status=active`, { withCredentials: true, headers: getAuthHeaders(), timeout: API_TIMEOUT }),
          axios.get(`${API}/subscription/status`, { withCredentials: true, headers: getAuthHeaders(), timeout: API_TIMEOUT }),
          axios.get(`${API}/market-hours`, { withCredentials: true, headers: getAuthHeaders(), timeout: API_TIMEOUT }),
          axios.get(`${API}/signals/performance/stats`, { timeout: API_TIMEOUT }),
          axios.get(`${API}/auth/me`, { withCredentials: true, headers: getAuthHeaders(), timeout: API_TIMEOUT })
        ]);

        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (signalsRes.status === 'fulfilled') setRecentSignals(signalsRes.value.data?.signals || []);
        
        if (subRes.status === 'fulfilled') {
          const subData = subRes.value.data;
          setIsPro(subData.status === 'active' && !subData.is_trial);
          setIsTrial(subData.is_trial === true);
          setTrialDaysLeft(subData.days_remaining || 0);
        }
        
        if (hoursRes.status === 'fulfilled') setMarketHours(hoursRes.value.data);
        if (perfRes.status === 'fulfilled') setSignalPerformance(perfRes.value.data);
        if (profileRes.status === 'fulfilled') setUserProfile(profileRes.value.data);

      } catch (err) {
        setLoadingError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    trackActivity('page_view', { page: 'dashboard' });
  }, []);

  // Fetch real-time prices
  const fetchRealPrices = async () => {
    setPricesLoading(true);
    setPricesError(null);
    try {
      const response = await axios.get(`${API}/real-prices`, { timeout: API_TIMEOUT });
      if (response.data?.prices) {
        const newPrices = response.data.prices.map(p => {
          const prevPrice = prevPricesRef.current[p.name];
          const isMovingUp = prevPrice ? p.raw_price > prevPrice : undefined;
          return { ...p, isMovingUp };
        });
        
        response.data.prices.forEach(p => {
          prevPricesRef.current[p.name] = p.raw_price;
        });
        
        setLiveMarkets(newPrices);
        setLastUpdate(new Date());
      }
    } catch (err) {
      setPricesError('Using cached prices');
    } finally {
      setPricesLoading(false);
    }
  };

  useEffect(() => {
    fetchRealPrices();
    const interval = setInterval(fetchRealPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060A10] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-2 border-cyan-500/20 rounded-full" />
            <div className="absolute inset-0 border-2 border-transparent border-t-cyan-500 rounded-full animate-spin" />
          </div>
          <p className="text-slate-400 font-mono text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060A10]" data-testid="dashboard-page">
      {/* Welcome Tour */}
      {showTour && <WelcomeTour onComplete={() => setShowTour(false)} />}

      {/* Top Bar - Market Ticker */}
      <div className="bg-[#0A0E14] border-b border-[#1E293B] px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6 overflow-x-auto">
            {liveMarkets.map((market, idx) => (
              <div key={idx} className="flex items-center gap-3 min-w-fit">
                <span className="text-white font-mono font-bold text-sm">{market.name}</span>
                <span className={`font-mono text-sm ${market.trend === 'up' ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                  {market.price}
                </span>
                <span className={`text-xs font-mono ${market.trend === 'up' ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                  {market.change}
                </span>
                {idx < liveMarkets.length - 1 && <div className="w-px h-4 bg-[#1E293B]" />}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
            <div className={`w-2 h-2 rounded-full ${marketHours?.is_open ? 'bg-[#00E676] animate-pulse' : 'bg-[#FF3B30]'}`} />
            <span>{marketHours?.is_open ? 'Markets Open' : 'Markets Closed'}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 lg:p-6 space-y-6">
        {/* Error Banner */}
        {loadingError && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-sm">{loadingError}</span>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="ml-auto border-[#FF3B30]/30 text-[#FF3B30]">
              Refresh
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] text-white mb-1">
              Welcome back{userProfile?.name ? `, ${userProfile.name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-slate-400 text-sm">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isPro && !isTrial && (
              <Button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white font-semibold" data-testid="upgrade-banner-btn">
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
            {isTrial && (
              <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">
                <Clock className="w-3 h-3 mr-1" />
                {trialDaysLeft} days left
              </Badge>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Active Signals */}
          <Card className="bg-gradient-to-br from-[#0D131C] to-[#0A0E14] border-[#1E293B] p-5 hover:border-[#00E676]/30 transition-all group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-wider mb-2">Active Signals</p>
                <p className="text-4xl font-bold font-mono text-white" data-testid="stat-active-signals">{stats?.active_signals || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#00E676]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-[#00E676]" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="h-1 flex-1 bg-[#1E293B] rounded-full overflow-hidden">
                <div className="h-full bg-[#00E676] rounded-full" style={{width: `${Math.min((stats?.active_signals || 0) * 20, 100)}%`}} />
              </div>
              <span className="text-xs text-slate-500 font-mono">{stats?.active_signals || 0}/5</span>
            </div>
          </Card>

          {/* Win Rate */}
          <Card className="bg-gradient-to-br from-[#0D131C] to-[#0A0E14] border-[#1E293B] p-5 hover:border-cyan-500/30 transition-all group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-wider mb-2">Win Rate</p>
                <p className="text-4xl font-bold font-mono text-white">{signalPerformance?.win_rate || stats?.win_rate || 0}%</p>
              </div>
              <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-cyan-400" />
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              <span className="text-[#00E676]">+2.3%</span> vs last month
            </p>
          </Card>

          {/* Total Trades */}
          <Card className="bg-gradient-to-br from-[#0D131C] to-[#0A0E14] border-[#1E293B] p-5 hover:border-purple-500/30 transition-all group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-wider mb-2">Total Trades</p>
                <p className="text-4xl font-bold font-mono text-white" data-testid="stat-total-trades">{stats?.total_trades || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Across all markets
            </p>
          </Card>

          {/* Notifications */}
          <Card className="bg-gradient-to-br from-[#0D131C] to-[#0A0E14] border-[#1E293B] p-5 hover:border-[#FFCC00]/30 transition-all group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-wider mb-2">Unread</p>
                <p className="text-4xl font-bold font-mono text-white" data-testid="stat-unread-notifications">{stats?.unread_notifications || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#FFCC00]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Bell className="w-6 h-6 text-[#FFCC00]" />
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')} className="mt-2 -ml-2 text-xs text-slate-400 hover:text-white">
              View all <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </Card>
        </div>

        {/* AI Market Analyst (Claude) */}
        <AIMarketAnalyst />

        {/* Main Grid - 3 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Signals */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Markets */}
            <Card className="bg-[#0D131C] border-[#1E293B] overflow-hidden">
              <div className="p-4 border-b border-[#1E293B] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="font-bold text-white font-['Manrope']">Live Markets</h2>
                    <p className="text-xs text-slate-500 font-mono">
                      {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : 'Real-time prices'}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={fetchRealPrices} disabled={pricesLoading} className="text-slate-400 hover:text-white">
                  <RefreshCw className={`w-4 h-4 ${pricesLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#1E293B]">
                {liveMarkets.map((market, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => { setChartMarket(market); setChartRange('24h'); }}
                    className={`p-4 hover:bg-[#152A4A]/50 transition-all cursor-pointer ${
                      market.isMovingUp === true ? 'bg-[#00E676]/5' : 
                      market.isMovingUp === false ? 'bg-[#FF3B30]/5' : ''
                    }`}
                    data-testid={`market-card-${market.name}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold font-mono text-sm">{market.name}</span>
                      {market.trend === 'up' ? (
                        <ArrowUpRight className="w-4 h-4 text-[#00E676]" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                      )}
                    </div>
                    <p className="text-2xl font-bold font-mono text-white mb-1" data-testid={`market-price-${market.name}`}>
                      {market.price}
                    </p>
                    <p className={`text-sm font-mono ${market.trend === 'up' ? 'text-[#00E676]' : 'text-[#FF3B30]'}`} data-testid={`market-change-${market.name}`}>
                      {market.change}
                    </p>
                    <div className="mt-2 -mx-1">
                      <Sparkline symbol={market.name} trend={market.trend} width={140} height={32} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Recent Signals */}
            <Card className="bg-[#0D131C] border-[#1E293B] overflow-hidden">
              <div className="p-4 border-b border-[#1E293B] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#00E676]/20 to-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Flame className="w-5 h-5 text-[#00E676]" />
                  </div>
                  <div>
                    <h2 className="font-bold text-white font-['Manrope']">Active Signals</h2>
                    <p className="text-xs text-slate-500">Latest trading opportunities</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/signals')} className="text-cyan-400 hover:text-cyan-300" data-testid="view-all-signals-btn">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-[#1E293B]">
                {recentSignals.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-[#1E293B] rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-slate-500" />
                    </div>
                    <p className="text-slate-400 mb-2">No active signals</p>
                    <p className="text-sm text-slate-500">Check back soon for new opportunities</p>
                  </div>
                ) : (
                  recentSignals.slice(0, 4).map((signal) => (
                    <div 
                      key={signal.signal_id}
                      onClick={() => navigate(`/signals/${signal.signal_id}`)}
                      className="p-4 hover:bg-[#152A4A]/50 transition-all cursor-pointer group"
                      data-testid={`signal-card-${signal.signal_id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            signal.direction === 'buy' ? 'bg-[#00E676]/10' : 'bg-[#FF3B30]/10'
                          }`}>
                            {signal.direction === 'buy' ? (
                              <TrendingUp className="w-5 h-5 text-[#00E676]" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-[#FF3B30]" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold font-mono text-white">{signal.market}</span>
                              <Badge className={`text-[10px] font-mono ${
                                signal.direction === 'buy' 
                                  ? 'bg-[#00E676]/10 text-[#00E676] border-[#00E676]/20' 
                                  : 'bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/20'
                              }`}>
                                {signal.direction.toUpperCase()}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{typeof signal.strategy === 'string' ? signal.strategy : 'Technical Analysis'}</p>
                          </div>
                        </div>
                        <Badge className="bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/20 font-mono text-[10px]">
                          {signal.confidence}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 pl-13">
                        <div className="bg-[#0A0E14] rounded-lg p-2">
                          <p className="text-[10px] text-slate-500 uppercase font-mono mb-1">Entry</p>
                          <p className="text-sm font-mono text-white font-bold">{signal.entry_zone}</p>
                        </div>
                        <div className="bg-[#0A0E14] rounded-lg p-2">
                          <p className="text-[10px] text-slate-500 uppercase font-mono mb-1">Stop Loss</p>
                          <p className="text-sm font-mono text-[#FF3B30] font-bold">{signal.stop_loss}</p>
                        </div>
                        <div className="bg-[#0A0E14] rounded-lg p-2">
                          <p className="text-[10px] text-slate-500 uppercase font-mono mb-1">Take Profit</p>
                          <p className="text-sm font-mono text-[#00E676] font-bold">{signal.take_profit}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Live News Grid */}
            <LiveStreamGrid />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="bg-[#0D131C] border-[#1E293B] p-4">
              <h3 className="font-bold text-white mb-4 font-['Manrope']">Quick Actions</h3>
              <div className="space-y-2">
                <Button 
                  onClick={() => navigate('/demo-trading')} 
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white justify-start"
                  data-testid="nav-demo-trading"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Paper Trading
                </Button>
                <Button 
                  onClick={() => navigate('/signals')} 
                  variant="outline" 
                  className="w-full border-[#1E293B] hover:bg-[#152A4A] text-white justify-start"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View All Signals
                </Button>
              </div>
            </Card>

            {/* Market Sessions */}
            {marketHours && (
              <Card className="bg-[#0D131C] border-[#1E293B] p-4">
                <h3 className="font-bold text-white mb-4 font-['Manrope']">Market Sessions</h3>
                <div className="space-y-3">
                  {marketHours.sessions && Object.entries(marketHours.sessions).map(([session, info]) => (
                    <div key={session} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${info.status === 'open' ? 'bg-[#00E676] animate-pulse' : 'bg-slate-600'}`} />
                        <span className="text-sm text-slate-300 capitalize">{session}</span>
                      </div>
                      <Badge className={`text-[10px] font-mono ${
                        info.status === 'open' 
                          ? 'bg-[#00E676]/10 text-[#00E676]' 
                          : 'bg-slate-700 text-slate-400'
                      }`}>
                        {info.status === 'open' ? 'OPEN' : 'CLOSED'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Signal Performance */}
            {signalPerformance && (
              <Card className="bg-[#0D131C] border-[#1E293B] p-4">
                <h3 className="font-bold text-white mb-4 font-['Manrope']">Performance</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Total Signals</span>
                    <span className="font-mono text-white font-bold">{signalPerformance.total_signals || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Winning</span>
                    <span className="font-mono text-[#00E676] font-bold">{signalPerformance.winning_signals || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Losing</span>
                    <span className="font-mono text-[#FF3B30] font-bold">{signalPerformance.losing_signals || 0}</span>
                  </div>
                  <div className="pt-3 border-t border-[#1E293B]">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">Win Rate</span>
                      <span className="font-mono text-cyan-400 font-bold text-lg">{signalPerformance.win_rate || 0}%</span>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Premium Features */}
            <Card className="bg-gradient-to-br from-[#0D131C] to-[#0A0E14] border-[#1E293B] p-4">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-bold text-white font-['Manrope']">Premium Features</h3>
              </div>
              <div className="space-y-2">
                {[
                  { name: 'Economic Calendar', icon: Calendar, locked: !isPro && !isTrial },
                  { name: 'Market Sentiment', icon: Gauge, locked: !isPro && !isTrial },
                  { name: 'Trading Journal', icon: BookOpen, locked: !isPro && !isTrial },
                  { name: 'Performance Stats', icon: BarChart3, locked: !isPro && !isTrial }
                ].map((feature, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      feature.locked ? 'opacity-50' : 'bg-[#152A4A]/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <feature.icon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-300">{feature.name}</span>
                    </div>
                    {feature.locked ? (
                      <Shield className="w-4 h-4 text-slate-500" />
                    ) : (
                      <Badge className="bg-[#00E676]/10 text-[#00E676] text-[10px]">Active</Badge>
                    )}
                  </div>
                ))}
              </div>
              {!isPro && !isTrial && (
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="w-full mt-4 bg-gradient-to-r from-[#FFCC00] to-orange-500 text-black font-bold hover:opacity-90"
                >
                  Unlock All Features
                </Button>
              )}
            </Card>
          </div>
        </div>

        {/* Premium Components - Full Width */}
        {(isPro || isTrial) && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ErrorBoundary fallback={<PremiumPlaceholder title="Economic Calendar" />}>
                <EconomicCalendar />
              </ErrorBoundary>
              <ErrorBoundary fallback={<PremiumPlaceholder title="Market Sentiment" />}>
                <MarketSentiment />
              </ErrorBoundary>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ErrorBoundary fallback={<PremiumPlaceholder title="Trading Journal" />}>
                <TradingJournal />
              </ErrorBoundary>
              <ErrorBoundary fallback={<PremiumPlaceholder title="Performance Dashboard" />}>
                <PerformanceDashboard />
              </ErrorBoundary>
            </div>
          </div>
        )}
      </div>

      {/* Market Chart Modal (sparkline with time-range toggle) */}
      <Dialog open={!!chartMarket} onOpenChange={(o) => { if (!o) setChartMarket(null); }}>
        <DialogContent className="bg-[#0D131C] border-[#1E293B] max-w-2xl" data-testid="market-chart-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-mono">{chartMarket?.name}</span>
                {chartMarket && (
                  <span className={`text-sm font-mono ${chartMarket.trend === 'up' ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                    {chartMarket.price} · {chartMarket.change}
                  </span>
                )}
              </div>
              <div className="flex gap-1" data-testid="chart-range-toggles">
                {['1h', '24h', '7d'].map((r) => (
                  <button
                    key={r}
                    onClick={() => setChartRange(r)}
                    data-testid={`chart-range-${r}`}
                    className={`px-3 py-1 rounded-md text-xs font-medium border transition ${
                      chartRange === r
                        ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                        : 'bg-transparent border-[#1E293B] text-slate-400 hover:text-white'
                    }`}
                  >
                    {r.toUpperCase()}
                  </button>
                ))}
              </div>
            </DialogTitle>
          </DialogHeader>
          {chartMarket && (
            <div className="mt-2 p-4 rounded-lg bg-[#060A10] border border-[#1E293B]">
              <Sparkline
                symbol={chartMarket.name}
                trend={chartMarket.trend}
                range={chartRange}
                width={640}
                height={220}
                strokeWidth={2}
                className="w-full"
              />
              <p className="mt-3 text-xs text-slate-500">
                Live price snapshots · window: <span className="text-slate-300">{chartRange}</span> · auto-refresh every 60s
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
